import { useEffect, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEmail, emailKindLabel } from '../../api/admin.js';
import { formatDateTime } from '../../lib/format.js';

interface EmailDetailModalProps {
  emailId: string;
  onClose: () => void;
}

/**
 * Detalle de un envío + preview del email EXACTO que se mandó. El HTML se
 * renderiza en un `<iframe sandbox>` (sin scripts ni navegación): el email trae
 * su propio `<html>`/estilos, así que aislarlo evita que pise el CSS del BO y
 * neutraliza cualquier HTML peligroso. Para envíos históricos/pending el
 * contenido no fue capturado (migración 0244) → mostramos un aviso.
 */
export function EmailDetailModal({ emailId, onClose }: EmailDetailModalProps): ReactElement {
  const [view, setView] = useState<'html' | 'text'>('html');

  // Cerrar con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const query = useQuery({
    queryKey: ['admin', 'email', emailId],
    queryFn: () => fetchEmail(emailId),
  });

  const d = query.data;
  const hasContent = !!(d && (d.bodyHtml || d.bodyText));

  return (
    <div
      className="mm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="email-detail-modal"
    >
      <div
        className="mm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-detail-title"
        style={{ maxWidth: 760 }}
      >
        <header className="mm-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h3 id="email-detail-title" className="mm-title">
            {d ? (d.subject ?? emailKindLabel(d.kind)) : 'Email'}
          </h3>
          <button type="button" className="btn" onClick={onClose} data-testid="email-detail-close">
            Cerrar
          </button>
        </header>
        <div className="mm-body">
          {query.isPending ? (
            <div className="loading">Cargando…</div>
          ) : query.isError ? (
            <div className="mm-error" role="alert">
              {query.error.message}
            </div>
          ) : !d ? (
            <div className="mm-error">No encontrado</div>
          ) : (
            <>
              <div className="metrics-grid">
                <div className="metric">
                  <span className="k">Tipo</span>
                  <span className="v" style={{ fontSize: 14 }}>
                    {emailKindLabel(d.kind)}
                  </span>
                </div>
                <div className="metric">
                  <span className="k">Estado</span>
                  <span className="v" style={{ fontSize: 14 }}>
                    <span className={`badge ${d.status === 'sent' ? 'b-ok' : 'b-warn'}`}>
                      {d.status === 'sent' ? 'enviado' : 'pendiente'}
                    </span>
                  </span>
                </div>
                <div className="metric">
                  <span className="k">Destinatario</span>
                  <span className="v mono" style={{ fontSize: 13 }}>
                    {d.toEmail ?? d.userEmail ?? '—'}
                  </span>
                </div>
                <div className="metric">
                  <span className="k">Enviado</span>
                  <span className="v mono" style={{ fontSize: 13 }}>
                    {d.sentAt ? formatDateTime(d.sentAt) : '—'}
                  </span>
                </div>
                <div className="metric">
                  <span className="k">Creado</span>
                  <span className="v mono" style={{ fontSize: 13 }}>
                    {formatDateTime(d.createdAt)}
                  </span>
                </div>
                <div className="metric">
                  <span className="k">Período (dedupe)</span>
                  <span className="v mono" style={{ fontSize: 13 }} title={d.periodKey}>
                    {d.periodKey}
                  </span>
                </div>
              </div>

              {d.subject ? (
                <div className="metric">
                  <span className="k">Asunto</span>
                  <span className="v" style={{ fontSize: 14 }}>
                    {d.subject}
                  </span>
                </div>
              ) : null}

              {hasContent ? (
                <>
                  <div className="chip-group">
                    <button
                      type="button"
                      className={`chip${view === 'html' ? ' is-on' : ''}`}
                      onClick={() => setView('html')}
                      disabled={!d.bodyHtml}
                    >
                      HTML
                    </button>
                    <button
                      type="button"
                      className={`chip${view === 'text' ? ' is-on' : ''}`}
                      onClick={() => setView('text')}
                      disabled={!d.bodyText}
                    >
                      Texto plano
                    </button>
                  </div>
                  {view === 'html' && d.bodyHtml ? (
                    <iframe
                      title="Cuerpo del email"
                      sandbox=""
                      srcDoc={d.bodyHtml}
                      data-testid="email-detail-iframe"
                      style={{
                        width: '100%',
                        height: 460,
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-sm)',
                        background: '#fff',
                      }}
                    />
                  ) : (
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 460,
                        overflow: 'auto',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-sm)',
                        padding: 'var(--s-4)',
                        margin: 0,
                        fontSize: 13,
                      }}
                    >
                      {d.bodyText}
                    </pre>
                  )}
                </>
              ) : (
                <div className="mm-desc">
                  El contenido de este email no fue capturado (envío anterior a la
                  captura, o quedó <code>pendiente</code>). Sólo está disponible el
                  cuerpo de los emails enviados a partir de la captura.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
